/**
 * Minimal JSON fetch wrapper used by the admin React Query hooks.
 *
 * Calls the admin app's own same-origin `/api/*` proxy, throws on non-2xx, and
 * returns the parsed JSON body. In dev, Vite proxies these requests to the
 * main Worker; in deployed environments, the admin Worker forwards them.
 * Supabase bearer tokens are attached automatically when a session exists.
 *
 * @copyright 2025 Glimbr Pty Ltd
 * @version 2026-04-20-v2
 */
import { getAccessToken, handleAdminApiAuthError } from './auth';

export class HttpError extends Error {
    status: number;
    body: unknown;

    constructor(status: number, body: unknown, message?: string) {
        super(message ?? `HTTP ${status}`);
        this.name = 'HttpError';
        this.status = status;
        this.body = body;
    }
}

async function apiRequest<T>(
    method: 'GET' | 'POST' | 'PATCH' | 'DELETE',
    path: string,
    init?: RequestInit
): Promise<T> {
    const headers = new Headers(init?.headers);
    headers.set('accept', 'application/json');

    const accessToken = await getAccessToken();
    if (accessToken && !headers.has('authorization')) {
        headers.set('authorization', `Bearer ${accessToken}`);
    }

    const res = await fetch(path, {
        ...init,
        method,
        headers,
        credentials: 'omit'
    });

    const text = await res.text();
    let body: unknown;
    try {
        body = text ? JSON.parse(text) : null;
    } catch {
        body = text;
    }

    if (!res.ok) {
        if (path.startsWith('/api/admin/') && (res.status === 401 || res.status === 403)) {
            const detail =
                typeof body === 'object' &&
                body !== null &&
                'error' in body &&
                typeof body.error === 'string'
                    ? body.error
                    : undefined;
            handleAdminApiAuthError(res.status, detail);
        }
        throw new HttpError(res.status, body, `${method} ${path} → ${res.status}`);
    }
    return body as T;
}

/** GET a same-origin admin path and parse the JSON response. */
export async function apiGet<T>(path: string, init?: RequestInit): Promise<T> {
    return apiRequest<T>('GET', path, init);
}

/** POST JSON to a same-origin admin path and parse the JSON response. */
export async function apiPost<T>(path: string, json?: unknown, init?: RequestInit): Promise<T> {
    const headers = new Headers(init?.headers);
    headers.set('content-type', 'application/json');
    return apiRequest<T>('POST', path, {
        ...init,
        headers,
        body: json === undefined ? undefined : JSON.stringify(json)
    });
}

/** PATCH JSON to a same-origin admin path and parse the JSON response. */
export async function apiPatch<T>(path: string, json?: unknown, init?: RequestInit): Promise<T> {
    const headers = new Headers(init?.headers);
    headers.set('content-type', 'application/json');
    return apiRequest<T>('PATCH', path, {
        ...init,
        headers,
        body: json === undefined ? undefined : JSON.stringify(json)
    });
}

/** DELETE a same-origin admin path and parse the JSON response. */
export async function apiDelete<T>(path: string, init?: RequestInit): Promise<T> {
    return apiRequest<T>('DELETE', path, init);
}

/** GET a same-origin admin path and return text, preserving bearer-token auth. */
export async function apiDownloadText(path: string, accept = 'text/plain'): Promise<string> {
    const headers = new Headers();
    headers.set('accept', accept);

    const accessToken = await getAccessToken();
    if (accessToken) {
        headers.set('authorization', `Bearer ${accessToken}`);
    }

    const res = await fetch(path, {
        method: 'GET',
        headers,
        credentials: 'omit'
    });

    const text = await res.text();
    if (!res.ok) {
        if (path.startsWith('/api/admin/') && (res.status === 401 || res.status === 403)) {
            handleAdminApiAuthError(res.status, text);
        }
        throw new HttpError(res.status, text, `GET ${path} → ${res.status}`);
    }

    return text;
}

/** Best-effort extraction of a useful API error message for toasts. */
export function getHttpErrorMessage(error: unknown, fallback: string): string {
    if (error instanceof HttpError) {
        if (
            typeof error.body === 'object' &&
            error.body !== null &&
            'error' in error.body &&
            typeof error.body.error === 'string'
        ) {
            return error.body.error;
        }
        return error.message;
    }

    if (error instanceof Error && error.message.trim()) return error.message;
    return fallback;
}
